import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";

import Money from "../../components/Money";
import { useHouseState, useHouseDispatch } from "../../contexts/HouseContext";
import { PAYMENT_METHODS, MethodGlyph, paymentLabel } from "../ledger/components/paymentMethods";
import { computeMemberStats, initialsOf } from "./memberStats";

import "./PlayerDetail.css";

/**
 * Player detail — the head-to-head book on a single player. Recreates
 * `plans/dashboard-redesign/claude-design-source/project/v15-player.jsx`
 * in the production stack: a hero card with the all-time net and any
 * outstanding balance, a stat grid, the session history with sparkline
 * bars, and the payment-methods panel. Every figure here is computed from
 * the player's real transactions across the house's games.
 */
export default function PlayerDetail() {
    const { memberId } = useParams();
    const navigate = useNavigate();
    const { house, games } = useHouseState();
    const { loadGames, updateMember } = useHouseDispatch();
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        const gameIds = Object.keys(games);
        if (gameIds.length) loadGames(gameIds);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (!house) {
        return <div className="pd-loading">Loading…</div>;
    }

    const member = (house.members || []).find((m) => m && m.id === memberId);

    if (!member) {
        return (
            <main className="player-detail">
                <Link to="/players" className="pd-back">‹ Players</Link>
                <div className="pd-missing">That player isn't on this roster.</div>
            </main>
        );
    }

    const stats = computeMemberStats(member.id, games);

    return (
        <main className="player-detail">
            <header className="pd-mast">
                <div className="pd-mast-lead">
                    <Link to="/players" className="pd-back">‹ Players</Link>
                    <div className="pd-mast-accent">
                        {stats.gamesPlayed
                            ? `regular · ${stats.gamesPlayed} ${stats.gamesPlayed === 1 ? "session" : "sessions"}`
                            : "no sessions played yet"}
                    </div>
                    <h1>{renderName(member.name)}</h1>
                </div>
                <div className="pd-mast-actions">
                    <button
                        type="button"
                        className="pd-btn"
                        onClick={() => setEditing((v) => !v)}
                    >
                        {editing ? "Cancel" : "Edit name"}
                    </button>
                </div>
            </header>

            {editing && (
                <EditNamePanel
                    member={member}
                    onSave={async (name) => {
                        await updateMember({ id: member.id, name });
                        setEditing(false);
                    }}
                />
            )}

            <div className="pd-top">
                <HeroCard member={member} stats={stats} />
                <StatGrid stats={stats} />
            </div>

            <div className="pd-bottom">
                <section className="pd-panel">
                    <div className="pd-panel-head">
                        <h2>
                            Session <em>history</em>
                        </h2>
                        <span className="pd-panel-link">{stats.gamesPlayed} total</span>
                    </div>
                    <SessionHistory stats={stats} onOpen={(id) => navigate(`/games/${id}`)} />
                </section>

                <PaymentPanel stats={stats} />
            </div>
        </main>
    );
}

/* Italicize the last word of a name, matching the brand's display accent. */
function renderName(name = "") {
    const words = name.trim().split(/\s+/);
    if (words.length <= 1) return <em>{name}</em>;
    const lead = words.slice(0, -1).join(" ");
    const tail = words[words.length - 1];
    return (
        <>
            {lead} <em>{tail}.</em>
        </>
    );
}

const HeroCard = ({ member, stats }) => {
    const owesYou = stats.owed > 0;
    const youOwe = stats.owed < 0;
    const netClass = stats.allTimeNet > 0 ? "is-up" : stats.allTimeNet < 0 ? "is-down" : "is-zero";

    return (
        <div className="pd-hero">
            <div className="pd-hero-top">
                <span className="pd-hero-avatar" aria-hidden="true">
                    {initialsOf(member.name)}
                </span>
                <div>
                    <div className="pd-hero-name">{member.name}</div>
                    <div className="pd-hero-sub">
                        {stats.payments.length
                            ? `prefers ${paymentLabel(stats.payments[0].type)}`
                            : "no payments recorded"}
                    </div>
                </div>
            </div>

            <div className="pd-hero-figures">
                <div>
                    <div className="pd-figure-label">All-time net</div>
                    <div className={`pd-figure ${netClass}`}>
                        <Money amount={stats.allTimeNet} signed={stats.allTimeNet !== 0} />
                    </div>
                    <div className="pd-figure-sub">
                        across {stats.gamesPlayed} {stats.gamesPlayed === 1 ? "session" : "sessions"}
                    </div>
                </div>
                <div>
                    <div className="pd-figure-label">{youOwe ? "You owe" : "Owes you"}</div>
                    <div className={`pd-figure ${owesYou || youOwe ? "is-gold" : "is-zero"}`}>
                        <Money amount={Math.abs(stats.owed)} />
                    </div>
                    <div className="pd-figure-sub">
                        {owesYou || youOwe ? "◔ unsettled" : "all settled up"}
                    </div>
                </div>
            </div>
        </div>
    );
};

const StatGrid = ({ stats }) => {
    const cells = [
        {
            label: "Win rate",
            value: stats.gamesPlayed ? `${Math.round(stats.winRate * 100)}%` : "—",
            sub: stats.gamesPlayed ? `${stats.wins} of ${stats.gamesPlayed}` : "no data",
        },
        {
            label: "Avg buy-in",
            value: stats.gamesPlayed ? fmtMoney(stats.avgBuyIn) : "—",
            sub: stats.gamesPlayed ? "per session" : "no data",
        },
        {
            label: "Best night",
            value: stats.bestNight ? fmtSigned(stats.bestNight.net) : "—",
            sub: stats.bestNight ? sessionLabel(stats.bestNight) : "no wins yet",
            kind: stats.bestNight ? "up" : null,
        },
        {
            label: "Worst night",
            value: stats.worstNight ? fmtSigned(stats.worstNight.net) : "—",
            sub: stats.worstNight ? sessionLabel(stats.worstNight) : "no losses yet",
            kind: stats.worstNight ? "down" : null,
        },
        {
            label: "Sessions",
            value: stats.gamesPlayed || "—",
            sub: stats.gamesPlayed ? "lifetime" : "none yet",
        },
        {
            label: "Last played",
            value: stats.lastPlayed ? relativeLabel(stats.lastPlayed.date) : "—",
            sub: stats.lastPlayed ? netSubLabel(stats.lastPlayed.net) : "never",
        },
    ];

    return (
        <div className="pd-stats">
            {cells.map((c) => (
                <div className="pd-stat" key={c.label}>
                    <div className="pd-stat-label">{c.label}</div>
                    <div className={`pd-stat-value${c.kind ? ` is-${c.kind}` : ""}`}>{c.value}</div>
                    <div className="pd-stat-sub">{c.sub}</div>
                </div>
            ))}
        </div>
    );
};

const SessionHistory = ({ stats, onOpen }) => {
    if (!stats.sessions.length) {
        return <div className="pd-history-empty">No sessions on the books yet.</div>;
    }

    const peak = Math.max(1, ...stats.sessions.map((s) => Math.abs(s.net)));

    return (
        <div className="pd-history">
            {stats.sessions.map((s) => {
                const kind = s.net > 0 ? "up" : s.net < 0 ? "down" : "flat";
                const width = `${Math.min(48, (Math.abs(s.net) / peak) * 48)}%`;
                return (
                    <div
                        key={s.gameId}
                        className="pd-session"
                        role="button"
                        tabIndex={0}
                        onClick={() => onOpen(s.gameId)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                onOpen(s.gameId);
                            }
                        }}
                    >
                        <div className="pd-session-date">
                            <div className="pd-session-day">{shortDate(s.date)}</div>
                            <div className="pd-session-name">{s.description}</div>
                        </div>
                        <div className="pd-spark">
                            <div className="pd-spark-axis" />
                            <div
                                className={`pd-spark-bar is-${kind}`}
                                style={kind === "down" ? { right: "50%", width } : { left: "50%", width }}
                            />
                        </div>
                        <div className={`pd-session-meta${s.unsettled ? " is-unsettled" : ""}`}>
                            {s.unsettled ? "◔ unsettled" : "settled"}
                        </div>
                        <div className={`pd-session-net is-${kind}`}>
                            <Money amount={s.net} signed={s.net !== 0} />
                        </div>
                    </div>
                );
            })}
        </div>
    );
};

const PaymentPanel = ({ stats }) => {
    const used = new Set(stats.payments.map((p) => p.type));
    const ordered = [
        ...PAYMENT_METHODS.filter((m) => used.has(m.type)),
        ...stats.payments
            .filter((p) => !PAYMENT_METHODS.some((m) => m.type === p.type))
            .map((p) => ({ type: p.type, label: paymentLabel(p.type) })),
    ];
    const byType = Object.fromEntries(stats.payments.map((p) => [p.type, p]));

    return (
        <section className="pd-panel">
            <div className="pd-panel-head">
                <h2>
                    Payment <em>methods</em>
                </h2>
            </div>

            {ordered.length === 0 ? (
                <div className="pd-history-empty">No payments recorded yet.</div>
            ) : (
                <div className="pd-methods">
                    {ordered.map((m, i) => {
                        const tally = byType[m.type] || { total: 0, settled: 0 };
                        return (
                            <div className="pd-method" key={m.type}>
                                <span className={`pd-method-chip ${m.className || ""}`}>
                                    <MethodGlyph type={m.type} />
                                </span>
                                <div className="pd-method-info">
                                    <div className="pd-method-name">
                                        {m.label}
                                        {i === 0 && <span className="pd-method-pill">Primary</span>}
                                    </div>
                                    <div className="pd-method-handle">{tally.total} transactions</div>
                                </div>
                                <div className="pd-method-settled">
                                    ✓ {tally.settled} of {tally.total}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </section>
    );
};

function EditNamePanel({ member, onSave }) {
    const [name, setName] = useState(member.name);
    const [pending, setPending] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setPending(true);
        await onSave(name.trim());
        setPending(false);
    };

    return (
        <form className="pd-edit" onSubmit={submit}>
            <input
                className="form-control"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
                required
            />
            <button type="submit" className="pd-btn pd-btn-primary" disabled={pending}>
                {pending ? "Saving…" : "Save"}
            </button>
        </form>
    );
}

/* ---- formatting helpers ---- */

function fmtMoney(amount) {
    return `$${Math.round(Math.abs(amount)).toLocaleString("en-US")}`;
}

function fmtSigned(amount) {
    const sign = amount > 0 ? "+" : amount < 0 ? "−" : "";
    return `${sign}$${Math.round(Math.abs(amount)).toLocaleString("en-US")}`;
}

function shortDate(date) {
    const d = new Date(date);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
}

function sessionLabel(session) {
    return shortDate(session.date);
}

function netSubLabel(net) {
    if (net > 0) return "won";
    if (net < 0) return "lost";
    return "broke even";
}

function relativeLabel(date) {
    const d = new Date(date);
    if (isNaN(d.getTime())) return "—";
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days <= 0) return "today";
    if (days === 1) return "1 day";
    if (days < 14) return `${days} days`;
    if (days < 60) return `${Math.round(days / 7)} wks`;
    return `${Math.round(days / 30)} mo`;
}
